/* One API surface for both products, so the compact pages don't branch on
   Mass vs. Priority at every call. */

import { api } from '../../../api/client';
import type { ListDeleteResult, MassMessagesList, MessageTopic } from '../../../api/client';
import type { Kind, Row } from './model';

/** A message inside a list. Priority messages count individual sends separately,
 *  but "Sent N×" means the same thing for both: times_sent. */
export interface ListMsg { id: number; message_text: string; times_sent: number }

export type MsgList = MassMessagesList;
type RawCampaign = Awaited<ReturnType<typeof api.getMassMessageCampaigns>>[number] | Awaited<ReturnType<typeof api.getPriorityMassMessageCampaigns>>[number];

export interface MassApi {
  kind: Kind;
  getLists: () => Promise<MsgList[]>;
  createList: (data: { name: string; content_probability?: number }) => Promise<MsgList>;
  updateList: (listId: number, data: Partial<MsgList>) => Promise<unknown>;
  deleteList: (listId: number) => Promise<ListDeleteResult>;
  getMessages: (listId: number) => Promise<ListMsg[]>;
  addMessages: (listId: number, text: string) => Promise<unknown>;
  removeMessage: (listId: number, messageId: number) => Promise<unknown>;
  randomTexts: (topic: MessageTopic, count: number) => Promise<{ messages: string[] }>;
  getCampaigns: () => Promise<RawCampaign[]>;
  createCampaign: (body: Record<string, unknown>) => Promise<unknown>;
  updateCampaign: (id: number, body: Record<string, unknown>) => Promise<unknown>;
  toggleCampaign: (id: number, on: boolean) => Promise<unknown>;
  deleteCampaign: (id: number) => Promise<unknown>;
  getSchedule: (campaignId: number) => Promise<Row[]>;
  updateEntry: (id: number, data: { message_text?: string; content_id?: string; scheduled_send_at?: string; scheduled_delete_at?: string }) => Promise<Row>;
  /** Mass deletes the row; Priority cancels it (and returns the updated row). */
  removeEntry: (id: number) => Promise<Row | null>;
  regenerate: (campaignId: number) => ReturnType<typeof api.createScheduleUpdateRequest>;
}

export function massApi(kind: Kind, accountId: string): MassApi {
  const a = accountId;
  if (kind === 'priority') {
    return {
      kind,
      getLists: () => api.getPriorityMassMessagesLists(a),
      createList: (d) => api.createPriorityMassMessagesList(a, d),
      updateList: (id, d) => api.updatePriorityMassMessagesList(a, id, d),
      deleteList: (id) => api.deletePriorityMassMessagesList(a, id),
      getMessages: (id) => api.getPriorityMassMessagesInList(a, id),
      addMessages: (id, text) => api.addPriorityMassMessagesToList(a, id, text),
      removeMessage: (id, mid) => api.removePriorityMassMessageFromList(a, id, mid),
      randomTexts: (topic, n) => api.getRandomPublicPriorityMassMessageTexts(a, topic, n),
      getCampaigns: () => api.getPriorityMassMessageCampaigns(a),
      createCampaign: (b) => api.createPriorityMassMessageCampaign(a, b as Parameters<typeof api.createPriorityMassMessageCampaign>[1]),
      updateCampaign: (id, b) => api.updatePriorityMassMessageCampaign(a, id, b),
      toggleCampaign: (id, on) => api.togglePriorityMassMessageCampaign(a, id, on),
      deleteCampaign: (id) => api.deletePriorityMassMessageCampaign(a, id),
      getSchedule: (id) => api.getPriorityMassMessageCampaignSchedule(a, id),
      updateEntry: (id, d) => api.updatePriorityMassMessageScheduleEntry(a, id, d),
      removeEntry: (id) => api.cancelPriorityMassMessageScheduleEntry(a, id),
      regenerate: (id) => api.createScheduleUpdateRequest(a, id, true),
    };
  }
  return {
    kind,
    getLists: () => api.getMassMessagesLists(a),
    createList: (d) => api.createMassMessagesList(a, d),
    updateList: (id, d) => api.updateMassMessagesList(a, id, d),
    deleteList: (id) => api.deleteMassMessagesList(a, id),
    getMessages: (id) => api.getMassMessagesInList(a, id),
    addMessages: (id, text) => api.addMassMessagesToList(a, id, text),
    removeMessage: (id, mid) => api.removeMassMessageFromList(a, id, mid),
    randomTexts: (topic, n) => api.getRandomPublicMassMessageTexts(a, topic, n),
    getCampaigns: () => api.getMassMessageCampaigns(a),
    createCampaign: (b) => api.createMassMessageCampaign(a, b as Parameters<typeof api.createMassMessageCampaign>[1]),
    updateCampaign: (id, b) => api.updateMassMessageCampaign(a, id, b),
    toggleCampaign: (id, on) => api.toggleMassMessageCampaign(a, id, on),
    deleteCampaign: (id) => api.deleteMassMessageCampaign(a, id),
    getSchedule: (id) => api.getMassMessageCampaignSchedule(a, id),
    updateEntry: (id, d) => api.updateMassMessageScheduleEntry(a, id, d),
    removeEntry: async (id) => { await api.deleteMassMessageScheduleEntry(a, id); return null; },
    regenerate: (id) => api.createScheduleUpdateRequest(a, id, false),
  };
}
